import type { DocInterpretationLine, DocInterpretationRegion } from '../../../../../types/docInterpretation';

/**
 * Plasserer uthevingen av en dokumentlinje etter PDF-ens tekstlag i stedet for området AI-en anslo.
 *
 * AI-ens område (regionJson) er ofte én rad feil, særlig med mindre modeller. En digital PDF har derimot
 * eksakt posisjon for hver tekstbit. Vi leter opp linjens kjennetegn (batch, leverandørens varenummer, EAN,
 * vårt varenummer) på siden og uthever raden der det står. AI-ens område brukes når teksten ikke finnes
 * (skannet dokument uten tekstlag, eller verdien er tolket annerledes enn den står).
 */

/** En tekstbit på siden, i andeler av sidebredde/-høyde med origo øverst til venstre. */
export interface PdfTextItem {
  str: string;
  x: number;
  top: number;
  width: number;
  height: number;
}

/** Et kjennetegn for linjen og hvilken forekomst på siden linjen er (0 = første). */
export interface TextAnchor {
  value: string;
  occurrence: number;
}

const norm = (s: string) => s.trim().toLowerCase();
const isNumeric = (s: string) => /^[\d\s.,:%-]+$/.test(s.trim());

/** Kjennetegnene som leses i tekstlaget, mest entydige først. */
export function lineAnchorValues(line: DocInterpretationLine): string[] {
  const values = [line.docBatchNo, line.docSupplierItemNo, line.docEan, line.itemNo]
    .map(v => (v ?? '').trim())
    .filter(v => v.length >= 3);
  return Array.from(new Set(values));
}

/**
 * Kjennetegn med forekomst: står samme verdi på flere linjer på siden (samme vare i to batcher),
 * er dette linjens nummer blant dem i dokumentets rekkefølge.
 */
export function lineAnchors(line: DocInterpretationLine, lines: DocInterpretationLine[]): TextAnchor[] {
  const samePage = lines.filter(l => l.pageNo === line.pageNo && l.lineSeq < line.lineSeq);
  return lineAnchorValues(line).map(value => ({
    value,
    occurrence: samePage.filter(l => lineAnchorValues(l).some(v => norm(v) === norm(value))).length,
  }));
}

function matches(item: PdfTextItem, anchor: string): boolean {
  const text = norm(item.str);
  const a = norm(anchor);
  return text === a || text.split(/\s+/).includes(a);
}

const center = (i: PdfTextItem) => i.top + i.height / 2;

/** Tekstbitene på samme tekstlinje som `item`. */
function sameLine(items: PdfTextItem[], item: PdfTextItem): PdfTextItem[] {
  return items.filter(i => Math.abs(center(i) - center(item)) < item.height * 0.5);
}

/** Tekstlinjene på siden, ovenfra og ned. */
function textLines(items: PdfTextItem[]): PdfTextItem[][] {
  const sorted = [...items].sort((a, b) => center(a) - center(b));
  const result: PdfTextItem[][] = [];
  for (const item of sorted) {
    const last = result[result.length - 1];
    if (last && Math.abs(center(last[0]) - center(item)) < item.height * 0.5) last.push(item);
    else result.push([item]);
  }
  return result;
}

/**
 * Området for raden der linjens kjennetegn står, eller null når det ikke finnes i tekstlaget.
 * Raden avgrenses av nabo-radene (en ny verdi i første kolonne): alt som ligger nærmere denne raden
 * enn nabo-radene hører til den. Slik kommer en beskrivelse som bryter over flere tekstlinjer med,
 * også når den står både over og under varenummeret, mens neste rad, frakt og summer holdes utenfor.
 */
export function snapRegionToText(
  items: PdfTextItem[],
  anchors: TextAnchor[],
  fallback: DocInterpretationRegion | null
): DocInterpretationRegion | null {
  if (items.length === 0) return null;

  let hit: PdfTextItem | null = null;
  for (const anchor of anchors) {
    const found = items.filter(i => matches(i, anchor.value)).sort((a, b) => a.top - b.top);
    if (found.length === 0) continue;
    if (found.length > anchor.occurrence) {
      hit = found[anchor.occurrence];
    } else {
      // Flere linjer enn forekomster: nærmest AI-ens anslag
      const aiCenter = fallback ? fallback.y + fallback.height / 2 : 0;
      hit = found.reduce((best, i) => (Math.abs(center(i) - aiCenter) < Math.abs(center(best) - aiCenter) ? i : best));
    }
    break;
  }
  if (!hit) return null;

  const h = hit.height;
  const mainLine = sameLine(items, hit);
  const firstColumnX = Math.min(...mainLine.map(i => i.x));
  const mainCenter = center(hit);

  // Nabo-radene: nærmeste tekst i første kolonne over og under
  const rowStarts = items.filter(i => Math.abs(i.x - firstColumnX) < 0.01 && Math.abs(center(i) - mainCenter) >= h * 0.5);
  const above = rowStarts.filter(i => center(i) < mainCenter).map(center);
  const below = rowStarts.filter(i => center(i) > mainCenter).map(center);
  const upper = above.length ? (Math.max(...above) + mainCenter) / 2 : mainCenter - h * 2.5;
  const lower = below.length ? (Math.min(...below) + mainCenter) / 2 : mainCenter + h * 2.5;

  // Tekstlinjer innenfor raden: hovedlinjen, og linjer uten tall (brutt beskrivelse — ikke frakt eller summer)
  const row = textLines(items)
    .filter(line => line.some(i => mainLine.includes(i)) || !line.some(i => isNumeric(i.str)))
    .flat()
    .filter(i => center(i) > upper && center(i) < lower);

  const pad = h * 0.35;
  const left = Math.max(0, Math.min(...row.map(i => i.x)) - pad);
  const right = Math.min(1, Math.max(...row.map(i => i.x + i.width)) + pad);
  const top = Math.max(0, Math.min(...row.map(i => i.top)) - pad);
  const bottom = Math.min(1, Math.max(...row.map(i => i.top + i.height)) + pad);
  return { x: left, y: top, width: right - left, height: bottom - top };
}
